import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'

type IProp = {
    title: string
    total: number
    icon: string
    showStatus?: boolean
    titleStatus?: string
    status?: StatusProp[]
    titleStatus2?: string
    status2?: StatusProp[]
}

export const ListHeader = ({
    title,
    total,
    icon,
    status,
    titleStatus,
    status2,
    titleStatus2,
    showStatus = true
}: IProp): JSX.Element => {
    const [state, setState] = useState({ status: [], status2: [] })

    useEffect(() => {
        setState({ ...state, ...{ status, status2 } })
    }, [status, status2])

    return (
        <HeaderContainer>
            <TitleContainer>
                <Title title={title} total={total} icon={icon} />
            </TitleContainer>
            {showStatus && (
                <>
                    {' '}
                    <StatusContainer>
                        <div className="status-title"> {i18n(titleStatus)} </div>
                        <div className="container-item">
                            {state.status.map(
                                ({ id, icon, color, title, subtitle, subtitle2, background }): JSX.Element => {
                                    return (
                                        <Status
                                            key={id}
                                            id={id}
                                            icon={icon}
                                            color={color}
                                            title={title}
                                            subtitle={subtitle}
                                            subtitle2={subtitle2}
                                            background={background}
                                        />
                                    )
                                }
                            )}
                        </div>
                    </StatusContainer>
                    {status2 && (
                        <StatusContainer style={{ marginLeft: '40px' }}>
                            <div className="status-title"> {i18n(titleStatus2)} </div>
                            <div className="container-item">
                                {state.status2.map(
                                    ({ id, icon, color, title, subtitle, subtitle2, background }): JSX.Element => {
                                        return (
                                            <Status
                                                key={id}
                                                id={id}
                                                icon={icon}
                                                color={color}
                                                title={title}
                                                subtitle={subtitle}
                                                subtitle2={subtitle2}
                                                background={background}
                                            />
                                        )
                                    }
                                )}
                            </div>
                        </StatusContainer>
                    )}
                </>
            )}
        </HeaderContainer>
    )
}
export default ListHeader

const Title = ({ title, total, icon }: IProp): JSX.Element => {
    return (
        <>
            <div className="icon-container title-container">
                <i className={icon}></i>
            </div>
            <div className="description-container">
                <span className="description-container-title">{i18n(title)}</span>
                <span>
                    <span className="description-container-subtitle">{i18n('Total of')}</span>
                    <span className="description-container-subtitle2">{total}</span>
                </span>
            </div>
        </>
    )
}

export const Status = ({ icon, color, title, subtitle, subtitle2, background }: StatusProp): JSX.Element => {
    return (
        <>
            <StatusItem background={background} color={color} className="icon-container status-container">
                <i className={icon}></i>
            </StatusItem>
            <div className="description-container">
                <span className="description-container-title">{i18n(title)}</span>
                <span>
                    <span className="description-container-subtitle">{i18n(subtitle)}</span>
                    <span className="description-container-subtitle2">{subtitle2}</span>
                </span>
            </div>

            <div className="separator"></div>
        </>
    )
}

export type StatusItemProp = {
    background: string
    color: string
}
export type StatusProp = {
    id: number
    icon: string
    color: string
    title?: string
    subtitle?: string
    subtitle2?: string
    background: string
}

export const getStatus = (id: number, status: StatusProp[]): StatusProp => {
    return status.find(ele => ele.id === id)
}

const HeaderContainer = styled.div`
    height: 107px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid ${({ theme }) => theme['gray-400']};

    .icon-container {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
    }

    .description-container {
        display: flex;
        flex-direction: column;
        margin: 0 12px;
    }
`
const TitleContainer = styled.div`
    flex: 1;
    display: flex;
    align-items: center;

    .title-container {
        height: 48px;
        width: 48px;
        font-size: 22px;
        background-color: ${({ theme }) => theme['blue-700']};
        color: ${({ theme }) => theme['gray-0']};
    }

    .description-container {
        .description-container-title {
            font-size: 24px;
            font-weight: 600;
            /* margin-bottom: 6px; */
            color: ${({ theme }) => theme['gray-800']};
        }

        .description-container-subtitle {
            font-size: 12px;
            color: ${({ theme }) => theme['gray-700']};
            margin-right: 4px;
        }

        .description-container-subtitle2 {
            font-size: 15px;
            color: ${({ theme }) => theme['gray-800']};
            font-weight: 600;
        }
    }
`

const StatusContainer = styled.div`
    display: flex;
    flex-direction: column;

    .status-title {
        font-size: 12px;
        text-transform: uppercase;
        font-weight: 600;
        color: ${({ theme }) => theme['gray-700']};
        margin-bottom: 8px;
    }

    .status-container {
        height: 36px;
        width: 36px;
        font-size: 16px;
    }

    .container-item {
        display: flex;
        align-items: center;
        .separator {
            height: 28px;
            background: #c8ccd4;
            width: 1px;
            margin: 0 24px;

            &:last-child {
                display: none;
            }
        }
    }

    .description-container {
        .description-container-title {
            font-size: 12px;
            margin-bottom: 4px;
            color: ${({ theme }) => theme['gray-700']};
        }

        .description-container-subtitle {
            font-size: 16px;
            color: ${({ theme }) => theme['gray-800']};
            margin-right: 2px;
        }

        .description-container-subtitle2 {
            font-size: 11px;
            color: ${({ theme }) => theme['gray-700']};
        }
    }
`

const StatusItem = styled.div<StatusItemProp>`
    display: flex;
    background-color: ${({ theme, background }) => {
        const pallet = theme[background]
        return pallet ? pallet : background
    }};
    color: ${({ theme, color }) => {
        const pallet = theme[color]
        return pallet ? pallet : color
    }};
`
